// Running a city action: call the game, put the returned village_state into the cache (or refetch when the answer has
// none), show the game's prompts, and keep a "busy" label so buttons can show a spinner. One hook per screen.
import { useCallback, useState } from "react";
import type { Village, WithPrompts } from "@/api/types";
import { useVillageCache } from "./queries";
import { usePrompts } from "./prompts";

const hasVillageState = (r: unknown): r is { village_state: Village } => {
  const vs = (r as { village_state?: unknown } | null)?.village_state;
  return !!vs && typeof vs === "object" && Array.isArray((vs as Village).buildings);
};

export function useCityActions(wid: number, vid: number) {
  const cache = useVillageCache();
  const [busy, setBusy] = useState<string | null>(null);

  /** Runs `fn`; returns its answer, or undefined after showing the error. `label` marks which button is busy. */
  const run = useCallback(
    async <T extends WithPrompts | unknown>(label: string, fn: () => Promise<T>, opts: { refetch?: boolean; success?: string } = {}): Promise<T | undefined> => {
      setBusy(label);
      try {
        const r = await fn();
        if (hasVillageState(r)) cache.put(wid, vid, r.village_state);
        else if (opts.refetch !== false) void cache.invalidate(wid, vid);
        if (opts.success) usePrompts.getState().toast(opts.success, "success");
        return r;
      } catch (e) {
        usePrompts.getState().error(e);
        return undefined;
      } finally {
        setBusy(null);
      }
    },
    [cache, wid, vid],
  );

  return { run, busy, isBusy: (label: string) => busy === label, anyBusy: busy !== null };
}
